import type React from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { ColumnChartGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";

import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import type { IRankDistributionOverTimeData } from "../../types";

import { graphConfig } from "./RankingDistributionGraphConfig";
import { WIDGET_FOOTER_KEYWORDS_LINK_TEXT } from "./RankingDistributionGraphConsts";
import type { ITransformedItem } from "./RankingDistributionGraphTypes";
import { transformData } from "./RankingDistributionGraphUtils";

interface IRankingDistributionGraphProps {
    data: IRankDistributionOverTimeData;
    isLoading: boolean;
}
export const RankingDistributionGraph: React.FC<IRankingDistributionGraphProps> = (props) => {
    const { params } = useCurrentStateAndParams();
    const { key, duration, country, webSource, pageFilter } = params;
    const { data, isLoading } = props;
    const [transformedData, setTransformedData] = useState<ITransformedItem[]>([]);

    useEffect(() => {
        if (!isLoading && data) {
            setTransformedData(transformData(data));
        }
    }, [data, isLoading]);

    const link = proNavigator.href("organicsearch_website_search_ranking_distribution_v2", {
        key,
        webSource,
        country,
        mtd: false,
        pageFilter,
        duration,
        isWWW: "*",
    });
    const footerCTA = {
        type: buttonTypes.FLAT,
        text: translate(WIDGET_FOOTER_KEYWORDS_LINK_TEXT),
        link,
        onClick: () => {
            allTrackers.dispatchTrackEvent(
                "Internal Link",
                "click",
                "Ranking Opportunities/Ranking Distribution Over Time",
            );
        },
    };
    const areAllValuesEmpty = (obj: Record<string, object>): boolean => {
        const allDataZero = Object.values(obj).every((month) =>
            Object.values(month).every((metrics) => Object.values(metrics).every((value) => value === 0)),
        );
        return allDataZero || Object.values(obj).every((value) => Object.keys(value).length === 0);
    };
    return (
        <WidgetContainer
            isLoading={isLoading}
            hideHeader={true}
            loaderWidth={"100%"}
            loaderHeight={237}
            withBorder={false}
            noData={
                (!data && !isLoading) || (data?.monthlyDistributions && areAllValuesEmpty(data?.monthlyDistributions))
            }
            noDataTitle={translate("common.noresults")}
            noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
            minWidth={"100%"}
            footerButtonsConfig={[footerCTA]}
        >
            <div data-automation="ranking-distribution-graph">
                <ColumnChartGraph
                    data={transformedData}
                    shouldPresentLegend={true}
                    legendPosition={LEGEND_POSITIONS.RIGHT}
                    keepSameLegendsOnDataUpdate={false}
                    shouldPresentValueAtLegend={true}
                    graphConfig={graphConfig(data?.monthlyDistributions)}
                    legendHeaders={[translate("seo.overview.ranking.distributoin.graph.legend.title")]}
                    isToggleAllEnabled
                />
            </div>
        </WidgetContainer>
    );
};
